import { useEffect, useMemo, useRef, useState } from 'react';
import {
  DndContext,
  DragEndEvent,
  DragOverEvent,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  DragOverlay,
  pointerWithin,
  rectIntersection,
  CollisionDetection,
} from '@dnd-kit/core';
import { KanbanColumn } from '@/components/tasks/KanbanColumn';
import { KanbanCard } from '@/components/tasks/KanbanCard';

type KanbanTask = {
  id: string;
  title: string;
  priority?: string;
  dueDate?: string;
  assignee?: { id: string; name: string; avatarUrl?: string };
  assigneeId?: string;
  statusId?: string;
  statusConfig?: { id: string; name: string; color: string; isClosed: boolean };
  _count?: { comments?: number };
};

type StatusConfig = {
  id: string;
  name: string;
  slug: string;
  color: string;
  position: number;
  isDefault: boolean;
  isClosed: boolean;
};

interface KanbanBoardProps {
  tasks: KanbanTask[];
  statusConfigs: StatusConfig[];
  /** Stable key (e.g. task list id) used to persist collapsed columns per board. */
  boardKey?: string;
  /** When true, the column row fills its parent's height so columns scroll
   * vertically inside and the horizontal scrollbar stays within the viewport. */
  fillHeight?: boolean;
  projectMembers?: Array<{ user: { id: string; name: string; avatarUrl?: string } }>;
  onTaskClick?: (task: KanbanTask) => void;
  onTaskReorder?: (taskId: string, statusId: string, position: number) => void;
  onTaskAssigneeChange?: (taskId: string, assigneeId: string | null) => void;
  onTaskAssigneesChange?: (taskId: string, assigneeIds: string[]) => void;
  onTaskDueDateChange?: (taskId: string, dueDate: string | null) => void;
  onTaskPriorityChange?: (taskId: string, priority: string) => void;
  onAddTask?: (statusId?: string) => void;
  /** Bulk-selection mode: cards show a selection checkbox. */
  selectionMode?: boolean;
  selectedIds?: Set<string>;
  onSelectTask?: (taskId: string, shiftKey: boolean) => void;
  /** Drag one selected card to another lane → move all selected to that status. */
  onBulkTaskMove?: (taskIds: string[], statusId: string) => void;
}

function groupTasks(tasks: KanbanTask[], statusConfigs: StatusConfig[]) {
  const byStatus: Record<string, string[]> = {};
  for (const s of statusConfigs) byStatus[s.id] = [];
  for (const t of tasks) {
    const sid = t.statusId || t.statusConfig?.id;
    if (sid && byStatus[sid]) byStatus[sid].push(t.id);
  }
  return byStatus;
}

const collapsedStorageKey = (boardKey?: string) => `kanban-collapsed:${boardKey ?? 'default'}`;

export function KanbanBoard({
  tasks,
  statusConfigs,
  boardKey,
  fillHeight,
  projectMembers,
  onTaskClick,
  onTaskReorder,
  onTaskAssigneeChange,
  onTaskAssigneesChange,
  onTaskDueDateChange,
  onTaskPriorityChange,
  onAddTask,
  selectionMode,
  selectedIds,
  onSelectTask,
  onBulkTaskMove,
}: KanbanBoardProps) {
  // Collapsed columns, persisted per board (task list). A collapsed column
  // renders as a narrow vertical drop strip instead of a full-width column.
  const didInitCollapse = useRef(false);
  const [collapsedStatuses, setCollapsedStatuses] = useState<Set<string>>(() => {
    try {
      const saved = localStorage.getItem(collapsedStorageKey(boardKey));
      if (saved) {
        // A saved layout exists — respect it, skip the empty-column default.
        didInitCollapse.current = true;
        return new Set<string>(JSON.parse(saved));
      }
    } catch { /* ignore */ }
    return new Set<string>();
  });

  // First time a board is opened with no saved layout, default to collapsing
  // every column that has no tasks. Runs once, after tasks are available.
  useEffect(() => {
    if (didInitCollapse.current) return;
    if (tasks.length === 0) return; // wait for tasks (or a genuinely empty list)
    didInitCollapse.current = true;
    const groups = groupTasks(tasks, statusConfigs);
    const empty = statusConfigs
      .filter((s) => (groups[s.id]?.length ?? 0) === 0)
      .map((s) => s.id);
    if (empty.length > 0) setCollapsedStatuses(new Set(empty));
  }, [tasks, statusConfigs]);

  const toggleCollapse = (statusId: string) => {
    setCollapsedStatuses((prev) => {
      const next = new Set(prev);
      if (next.has(statusId)) next.delete(statusId);
      else next.add(statusId);
      try {
        localStorage.setItem(collapsedStorageKey(boardKey), JSON.stringify([...next]));
      } catch { /* ignore */ }
      return next;
    });
  };
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
    useSensor(KeyboardSensor)
  );

  // Local ordering state driven by drag events. We re-sync from props whenever
  // the task set changes (new task, delete, server-confirmed reorder, etc.).
  const [itemsByStatus, setItemsByStatus] = useState<Record<string, string[]>>(
    () => groupTasks(tasks, statusConfigs),
  );
  const isDraggingRef = useRef(false);

  useEffect(() => {
    // Don't clobber the in-flight optimistic order while the user is still dragging.
    if (isDraggingRef.current) return;
    setItemsByStatus(groupTasks(tasks, statusConfigs));
  }, [tasks, statusConfigs]);

  const tasksById = useMemo(() => {
    const m = new Map<string, KanbanTask>();
    for (const t of tasks) m.set(t.id, t);
    return m;
  }, [tasks]);

  const [draggedTaskId, setDraggedTaskId] = useState<string | null>(null);
  const draggedTask = draggedTaskId ? tasksById.get(draggedTaskId) : null;
  const groupDragCount =
    selectionMode && draggedTaskId && selectedIds?.has(draggedTaskId)
      ? (selectedIds?.size ?? 1)
      : 1;
  // During a group drag, hide every selected card except the one under the
  // cursor so the whole group visibly lifts out of its lanes.
  const hiddenIds =
    groupDragCount > 1
      ? new Set([...(selectedIds ?? [])].filter((id) => id !== draggedTaskId))
      : undefined;

  const findContainer = (id: string): string | null => {
    if (itemsByStatus[id]) return id;
    for (const [sid, ids] of Object.entries(itemsByStatus)) {
      if (ids.includes(id)) return sid;
    }
    return null;
  };

  // Prefer a card under the pointer so SortableContext can push items around
  // to "make room". If the pointer is only inside a column's empty area
  // (no card), fall back to the column so cross-column drops still register.
  const collisionDetection: CollisionDetection = (args) => {
    const pointerCollisions = pointerWithin(args);
    if (pointerCollisions.length > 0) {
      const cardHit = pointerCollisions.find(
        (c) => !statusConfigs.some((s) => s.id === c.id),
      );
      if (cardHit) return [cardHit];
      const columnHit = pointerCollisions.find((c) =>
        statusConfigs.some((s) => s.id === c.id),
      );
      return columnHit ? [columnHit] : pointerCollisions;
    }
    return rectIntersection(args);
  };

  const handleDragOver = (event: DragOverEvent) => {
    try {
    const { active, over } = event;
    if (!over) return;

    const activeId = active.id as string;
    const overId = over.id as string;
    if (activeId === overId) return;

    const activeContainer = findContainer(activeId);
    const overContainer = findContainer(overId);
    if (!activeContainer || !overContainer) return;

    setItemsByStatus((prev) => {
      const activeItems = prev[activeContainer];
      const overItems = prev[overContainer];
      const activeIndex = activeItems.indexOf(activeId);
      if (activeIndex === -1) return prev;

      if (activeContainer === overContainer) {
        // Same-column reorder — wait for drag end to commit; during dragOver
        // we only move cross-column. The sortable strategy handles intra-column
        // visual shifts via transforms without mutating our state.
        return prev;
      }

      // Cross-column: figure out insertion index in target column.
      let newIndex: number;
      if (overId === overContainer) {
        // Dropped on the column itself (e.g., empty column) — append.
        newIndex = overItems.length;
      } else {
        newIndex = overItems.indexOf(overId);
        if (newIndex === -1) newIndex = overItems.length;
      }

      return {
        ...prev,
        [activeContainer]: activeItems.filter((id) => id !== activeId),
        [overContainer]: [
          ...overItems.slice(0, newIndex),
          activeId,
          ...overItems.slice(newIndex),
        ],
      };
    });
    } catch (err) {
      console.error('[KanbanBoard] handleDragOver error:', err);
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    try {
    const { active, over } = event;
    const activeId = active.id as string;

    const cleanup = () => {
      isDraggingRef.current = false;
      setDraggedTaskId(null);
    };

    if (!over) {
      cleanup();
      return;
    }

    const overId = over.id as string;
    const activeContainer = findContainer(activeId);
    const overContainer = findContainer(overId);

    if (!activeContainer || !overContainer) {
      cleanup();
      return;
    }

    // Group move: dragging a selected card carries the whole selection to the
    // dropped-on lane (status). Only cross-lane drops act; same-lane just resets.
    const isGroupDrag =
      !!selectionMode && !!selectedIds?.has(activeId) && (selectedIds?.size ?? 0) > 1;
    if (isGroupDrag) {
      cleanup();
      const originalTask = tasksById.get(activeId);
      const originalContainer = originalTask?.statusId || originalTask?.statusConfig?.id;
      if (overContainer !== originalContainer) {
        const ids = [...(selectedIds as Set<string>)].filter((id) => tasksById.has(id));
        // Optimistic local move: pull the selection out of every lane, append
        // to the target. The parent's optimistic cache update then re-syncs.
        setItemsByStatus((prev) => {
          const sel = new Set(ids);
          const next: Record<string, string[]> = {};
          for (const [sid, list] of Object.entries(prev)) next[sid] = list.filter((id) => !sel.has(id));
          next[overContainer] = [...(next[overContainer] || []), ...ids];
          return next;
        });
        onBulkTaskMove?.(ids, overContainer);
      } else {
        setItemsByStatus(groupTasks(tasks, statusConfigs));
      }
      return;
    }

    // Snapshot current local state (already reflects any cross-column moves
    // from onDragOver). Compute the final index in the target column, then
    // update local state and notify the parent outside of setState.
    const currentItems = itemsByStatus[overContainer] || [];
    let nextItemsByStatus = itemsByStatus;
    let finalIndex: number;

    if (activeContainer === overContainer) {
      const sourceIndex = currentItems.indexOf(activeId);
      let targetIndex: number;
      if (overId === overContainer) {
        targetIndex = currentItems.length - 1;
      } else {
        targetIndex = currentItems.indexOf(overId);
        if (targetIndex === -1) targetIndex = currentItems.length - 1;
      }

      if (sourceIndex === -1) {
        finalIndex = targetIndex >= 0 ? targetIndex : 0;
      } else if (sourceIndex === targetIndex) {
        finalIndex = sourceIndex;
      } else {
        const moved = [...currentItems];
        moved.splice(sourceIndex, 1);
        moved.splice(targetIndex, 0, activeId);
        nextItemsByStatus = { ...itemsByStatus, [overContainer]: moved };
        finalIndex = targetIndex;
      }
    } else {
      // Cross-column move was already applied in onDragOver — read the index.
      finalIndex = currentItems.indexOf(activeId);
      if (finalIndex === -1) finalIndex = 0;
    }

    if (nextItemsByStatus !== itemsByStatus) {
      setItemsByStatus(nextItemsByStatus);
    }

    cleanup();

    // Compare against server-truth position to decide if we should commit.
    const originalTask = tasksById.get(activeId);
    const originalContainer = originalTask?.statusId || originalTask?.statusConfig?.id;
    const originalGroups = groupTasks(tasks, statusConfigs);
    const originalIndex = originalContainer
      ? (originalGroups[originalContainer]?.indexOf(activeId) ?? -1)
      : -1;
    const hasMoved =
      overContainer !== originalContainer || finalIndex !== originalIndex;
    if (hasMoved && finalIndex >= 0) {
      onTaskReorder?.(activeId, overContainer, finalIndex);
    }
    } catch (err) {
      console.error('[KanbanBoard] handleDragEnd error:', err);
      isDraggingRef.current = false;
      setDraggedTaskId(null);
      setItemsByStatus(groupTasks(tasks, statusConfigs));
    }
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisionDetection}
      onDragStart={(event) => {
        isDraggingRef.current = true;
        setDraggedTaskId(event.active.id as string);
      }}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
      onDragCancel={() => {
        isDraggingRef.current = false;
        setDraggedTaskId(null);
        setItemsByStatus(groupTasks(tasks, statusConfigs));
      }}
    >
      <div className={`flex gap-4 overflow-x-auto pb-4 -mx-6 px-6${fillHeight ? ' h-full' : ''}`}>
        {statusConfigs
          .slice()
          .sort((a, b) => a.position - b.position)
          .map((status) => {
            const ids = itemsByStatus[status.id] || [];
            const columnTasks = ids
              .map((id) => tasksById.get(id))
              .filter((t): t is KanbanTask => !!t);
            return (
              <KanbanColumn
                key={status.id}
                status={status}
                tasks={columnTasks}
                collapsed={collapsedStatuses.has(status.id)}
                onToggleCollapse={() => toggleCollapse(status.id)}
                projectMembers={projectMembers}
                onTaskClick={onTaskClick}
                onTaskAssigneeChange={onTaskAssigneeChange}
                onTaskAssigneesChange={onTaskAssigneesChange}
                onTaskDueDateChange={onTaskDueDateChange}
                onTaskPriorityChange={onTaskPriorityChange}
                onAddTask={() => onAddTask?.(status.id)}
                selectionMode={selectionMode}
                selectedIds={selectedIds}
                onSelectTask={onSelectTask}
                hiddenIds={hiddenIds}
              />
            );
          })}
      </div>

      <DragOverlay dropAnimation={null}>
        {draggedTask ? (
          <div className="relative">
            {groupDragCount > 1 && (
              <>
                {/* Stacked "cards behind" hint for a group drag */}
                <div className="absolute inset-0 translate-x-1.5 translate-y-1.5 rounded-lg border border-grey-200 bg-white shadow" />
                <span className="absolute -top-2 -right-2 z-10 flex h-6 min-w-[1.5rem] items-center justify-center rounded-full bg-accent px-1.5 text-xs font-semibold text-white shadow">
                  {groupDragCount}
                </span>
              </>
            )}
            <KanbanCard
              task={draggedTask}
              projectMembers={projectMembers}
              onAssigneeChange={onTaskAssigneeChange ? () => {} : undefined}
            onAssigneesChange={onTaskAssigneesChange ? () => {} : undefined}
              onDueDateChange={onTaskDueDateChange ? () => {} : undefined}
              onPriorityChange={onTaskPriorityChange ? () => {} : undefined}
              isDragging
            />
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
